import React from "react";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@material-ui/core";
import { Controller, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { AsyncAutocomplete, Select, FormControl, TextField } from "components";
import { emailTypes, proofTypes } from "validation";
import { TemplateEditorFormSchema } from "validation/components/TemplateEditor/templateEditorFormRules";
import { SelectOptions } from "hooks";
import { getAllUserGroups } from "store/features/templates/overivew";
import { Autocomplete } from "@material-ui/lab";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useTemplateAutocomplete } from "hooks/api/useTemplateAutocomplete";

interface TemplateFormProps {
    isNew?: boolean;
    // eslint-disable-next-line
    onMaterialSpecSelect(onChange: (...event: any[]) => void, option: SelectOptions[] | null): void;
}

export const TemplateEditorForm = ({ isNew = false, onMaterialSpecSelect }: TemplateFormProps) => {
    const { t } = useTranslation();
    const { control, errors } = useFormContext<TemplateEditorFormSchema>();
    const allUserGroups = useSelector(getAllUserGroups);
    const searchFn = useTemplateAutocomplete();

    return (
        <form>
            <Box display="flex" pb={3}>
                <FormControl>
                    <Controller
                        name="name"
                        control={control}
                        as={
                            <TextField
                                disabled={!isNew}
                                label="Template name"
                                error={!!errors.name}
                                helperText={errors.name?.message}
                            />
                        }
                    />
                </FormControl>

                <FormControl>
                    <Controller
                        name="userGroups"
                        defaultValue={[]}
                        control={control}
                        render={({ onChange, value }) => {
                            return (
                                <Autocomplete
                                    multiple={true}
                                    value={value}
                                    options={allUserGroups}
                                    onChange={(event, value) => onChange(value)}
                                    getOptionLabel={(option) => option.name}
                                    getOptionSelected={(option, value) => option.name === value.name}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Set user groups" placeholder="User groups..." />
                                    )}
                                />
                            );
                        }}
                    />
                </FormControl>
                <FormControl disableMargin>
                    <AsyncAutocomplete
                        disableMargin={true}
                        name="materialSpec"
                        onChange={onMaterialSpecSelect}
                        minLength={3}
                        multiple={false}
                        searchFn={searchFn}
                    />
                </FormControl>
            </Box>
            <Accordion defaultExpanded variant="outlined">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body1" color="textSecondary">
                        {t("templates.overview.label.properties")}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ alignItems: "flex-start" }}>
                    <Box display="flex" flexDirection="column" flex={1}>
                        <FormControl>
                            <Controller
                                name="from"
                                control={control}
                                as={<TextField label="From" error={!!errors.from} helperText={errors.from?.message} />}
                            />
                        </FormControl>
                        <FormControl>
                            <Controller
                                name="to"
                                control={control}
                                as={<TextField label="To" error={!!errors.to} helperText={errors.to?.message} />}
                            />
                        </FormControl>
                        <FormControl>
                            <Controller
                                name="subject"
                                control={control}
                                as={
                                    <TextField
                                        label="Subject"
                                        error={!!errors.subject}
                                        helperText={errors.subject?.message}
                                    />
                                }
                            />
                        </FormControl>
                        <FormControl>
                            <Controller name="Reply to" control={control} as={<TextField label="Reply to" />} />
                        </FormControl>
                        <FormControl>
                            <Controller name="cc" control={control} as={<TextField label="CC" />} />
                        </FormControl>
                        <FormControl>
                            <Controller name="bcc" control={control} as={<TextField label="BCC" />} />
                        </FormControl>
                    </Box>
                    <Box display="flex" flexDirection="column" flex={1}>
                        <Select name="emailType" title="Email type" options={emailTypes} />
                        <Select name="proofType" title="Proof type" options={proofTypes} />
                        <FormControl>
                            <Controller name="language" control={control} as={<TextField label="Language" />} />
                        </FormControl>
                        <FormControl>
                            <Controller
                                name="status"
                                control={control}
                                as={<TextField type="number" label="Set status" />}
                            />
                        </FormControl>
                    </Box>
                    <Box display="flex" flexDirection="column" flex={1}>
                        <FormControl disableMargin>
                            <Controller name="cellPhone" control={control} as={<TextField label="Phone no." />} />
                        </FormControl>
                        <FormControl disableMargin>
                            <Controller name="smsText" control={control} as={<TextField label="Text message" />} />
                        </FormControl>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </form>
    );
};
